import React, { useState, useRef, useMemo } from "react";
import { useQuery, useMutation } from "react-query";
import { getCMSList, createCMS, editCMS, deleteCMS, handleCMSStatus } from "../../Services/CMS";
import { Success, Error, Confirmation } from "../../Components/Modal";
import Table from "../../Components/Table";
import StatusHandler from "../../Components/Table/StatusHandler";
import Modal from "react-bootstrap/Modal";
import { useForm, Controller } from "react-hook-form";
import JoditEditor from "jodit-react";
import { getSerialNumber } from "../../Utils/helpers";
import PageHeader from "../../Components/PageHeader";
import "./CMSManagement.css";

const PAGE_TYPES = [
  { value: "terms", label: "Terms & Conditions" },
  { value: "privacy", label: "Privacy Policy" },
  { value: "about", label: "About Us" },
];

const PAGE_TYPE_LABELS = {
  terms: "Terms",
  privacy: "Privacy",
  about: "About",
  custom: "Custom",
};

const DEFAULT_TITLES = {
  terms: "Terms and Conditions",
  privacy: "Privacy Policy",
  about: "About Us",
};

const isSystemPage = (item) =>
  Boolean(item?.isSystem) || ["terms", "privacy", "about"].includes(item?.pageKey);

export default function CMSManagement() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const { data: cmsData, isLoading, isFetching, refetch } = useQuery(
    ["cms_list", page, perPage, search],
    () => getCMSList(page, perPage, search)
  );

  const { mutate: mutateDelete } = useMutation((id) => deleteCMS(id), {
    onSuccess: () => {
      Success("Deleted", "CMS entry removed successfully.");
      refetch();
    },
    onError: (err) => Error(err?.response?.data?.message || "Error deleting CMS"),
  });

  const { mutate: mutateStatus } = useMutation((id) => handleCMSStatus(id), {
    onSuccess: () => {
      Success("Status Updated", "CMS status toggled successfully.");
      refetch();
    },
    onError: (err) => Error(err?.response?.data?.message || "Cannot update status"),
  });

  const handleEdit = (item) => {
    setEditData(item);
    setShowModal(true);
  };

  const handleDelete = (item) => {
    if (isSystemPage(item)) {
      Error("System pages (Terms / Privacy / About) cannot be deleted. Edit content instead.");
      return;
    }
    Confirmation("Are you sure you want to delete this CMS entry?", "Delete", () =>
      mutateDelete(item._id)
    );
  };

  const toggleStatus = (item) => {
    if (isSystemPage(item)) {
      Error("System pages must stay active and cannot be deactivated.");
      return;
    }
    Confirmation(
      `Are you sure you want to ${item.status ? "deactivate" : "activate"} ${item.title}?`,
      "Yes",
      () => mutateStatus(item._id)
    );
  };

  return (
    <div className="app-content content">
      <div className="content-wrapper">
        <div className="content-body">
          <section id="configuration">
            <div className="user-details-wrapper mt-0">
              <PageHeader
                title="CMS Management"
                subtitle="Manage Terms, Privacy, and About pages separately for User and Driver apps. These pages cannot be deleted or deactivated."
                // Add New Document disabled — only edit existing system pages
                // action={
                //   <button
                //     type="button"
                //     className="cms-add-btn d-inline-flex align-items-center"
                //     onClick={() => {
                //       setEditData(null);
                //       setShowModal(true);
                //     }}
                //   >
                //     <i className="fas fa-plus me-2" />
                //     Add New Document
                //   </button>
                // }
              />

              <Table
                tableTitles={["S.No.", "Title", "Type", "Roles/Target", "Status", "Created At", "Action"]}
                isLoading={isLoading}
                isFetching={isFetching}
                data={cmsData?.data?.data?.docs}
                totalDocs={cmsData?.data?.data?.totalDocs}
                totalPages={Math.ceil(cmsData?.data?.data?.totalPages || 0)}
                page={page}
                setPage={setPage}
                setSearchString={setSearch}
                perPage={perPage}
                setPerPage={setPerPage}
              >
                {cmsData?.data?.data?.docs?.map((item, index) => {
                  const system = isSystemPage(item);
                  return (
                    <tr key={item._id}>
                      <td>{getSerialNumber(cmsData?.data?.data, index)}</td>
                      <td>
                        <span className="fw-bold">{item.title}</span>
                        {system && <span className="cms-system-tag">System</span>}
                      </td>
                      <td>
                        <span className={`cms-type-badge cms-type-badge--${item.pageKey || "custom"}`}>
                          {PAGE_TYPE_LABELS[item.pageKey] || "Custom"}
                        </span>
                      </td>
                      <td>
                        {item.roles?.map((role) => (
                          <span
                            key={role}
                            className={`cms-role-badge ${
                              role === "user" ? "cms-role-badge--user" : "cms-role-badge--driver"
                            }`}
                          >
                            {role}
                          </span>
                        ))}
                      </td>
                      <td>
                        <span className={`status-pill ${item.status ? "status-active" : "status-inactive"}`}>
                          {item.status ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-muted small">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <StatusHandler
                          edit
                          active={!system}
                          item={{ ...item, active: item.status }}
                          onClick={() => toggleStatus(item)}
                          onEdit={() => handleEdit(item)}
                          onDelete={system ? undefined : () => handleDelete(item)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </Table>
            </div>
          </section>
        </div>
      </div>

      <CMSFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editData={editData}
        refresh={refetch}
      />
    </div>
  );
}

function CMSFormModal({ show, onHide, editData, refresh }) {
  const { register, handleSubmit, reset, control, watch, setValue } = useForm();
  const editor = useRef(null);
  const isExistingCustom = Boolean(editData) && !isSystemPage(editData);
  const systemLocked = isSystemPage(editData);

  const rolesAvailable = [
    { value: "user", label: "User App", icon: "fa-user" },
    { value: "driver", label: "Driver App", icon: "fa-truck" },
  ];

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: "Start typing your policy content...",
      height: 340,
      toolbarAdaptive: false,
      buttons:
        "bold,italic,underline,strikethrough,|,ul,ol,|,font,size,paragraph,|,align,|,link,image,table,|,undo,redo,|,hr,eraser,fullsize",
      uploader: {
        insertImageAsBase64URI: true,
      },
    }),
    []
  );

  React.useEffect(() => {
    if (editData) {
      reset({
        title: editData.title,
        content: editData.content,
        status: editData.status,
        roles: editData.roles || [],
        pageKey: editData.pageKey || "custom",
        targetRole: editData.roles?.[0] || "user",
      });
    } else {
      reset({
        title: DEFAULT_TITLES.terms,
        content: "",
        status: true,
        roles: ["user"],
        pageKey: "terms",
        targetRole: "user",
      });
    }
  }, [editData, reset, show]);

  const pageKey = watch("pageKey") || "terms";
  const selectedRoles = watch("roles") || [];
  const statusActive = watch("status");
  const targetRole = watch("targetRole") || "user";

  React.useEffect(() => {
    if (!show || editData || isExistingCustom) return;
    setValue("roles", [targetRole]);
    setValue("title", DEFAULT_TITLES[pageKey] || "");
    setValue("status", true);
  }, [pageKey, targetRole, show, editData, isExistingCustom, setValue]);

  const { mutate: mutateCMS, isLoading } = useMutation(
    (data) => {
      if (editData) return editCMS({ ...data, id: editData._id });
      return createCMS(data);
    },
    {
      onSuccess: () => {
        Success("Success", `CMS entry ${editData ? "updated" : "created"} successfully.`);
        refresh();
        onHide();
      },
      onError: (err) => Error(err?.response?.data?.message || "Error saving CMS"),
    }
  );

  const onSubmit = (data) => {
    const payload = isExistingCustom
      ? {
          title: data.title,
          content: data.content,
          pageKey: "custom",
          status: Boolean(data.status),
          roles: data.roles,
        }
      : {
          title: data.title,
          content: data.content,
          pageKey: data.pageKey,
          status: true,
          roles: [data.targetRole || "user"],
        };

    if (!payload.roles?.length) {
      Error("Select at least one target app");
      return;
    }

    if (!payload.content || !String(payload.content).replace(/<[^>]*>/g, "").trim()) {
      Error("Content is required");
      return;
    }

    mutateCMS(payload);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      className="cms-modal premium-cms-modal"
      scrollable
    >
      <Modal.Header closeButton className="cms-modal__header">
        <div className="cms-modal__header-main">
          <div className="cms-modal__header-icon">
            <i className="fas fa-file-contract" />
          </div>
          <div>
            <Modal.Title>{editData ? "Update Document" : "Create New Document"}</Modal.Title>
            <p className="cms-modal__subtitle">
              {isExistingCustom
                ? "Existing custom document — you can edit, deactivate, or delete it."
                : "Legal page for one app only (User or Driver). Cannot delete or deactivate."}
            </p>
          </div>
        </div>
      </Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body className="cms-modal__body">
          <div className="cms-form-grid">
            <div className="cms-form-block">
              <label className="cms-field-label">
                Document Type <span className="text-danger">*</span>
              </label>
              {isExistingCustom ? (
                <input className="cms-input" value="Custom Document" disabled readOnly />
              ) : (
                <select
                  className="cms-input"
                  {...register("pageKey", { required: true })}
                  disabled={systemLocked}
                >
                  {PAGE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="cms-form-block">
              <label className="cms-field-label">
                Document Title <span className="text-danger">*</span>
              </label>
              <input
                className="cms-input"
                {...register("title", { required: true })}
                placeholder="e.g. Privacy Policy"
              />
            </div>
          </div>

          <div className="cms-settings-panel">
            <div className="cms-settings-panel__col">
              <span className="cms-settings-label">
                {isExistingCustom ? "Target Apps" : "Target App (one only)"}
              </span>

              {isExistingCustom ? (
                <div className="cms-app-pills">
                  {rolesAvailable.map((role) => {
                    const isSelected = selectedRoles.includes(role.value);
                    return (
                      <label
                        key={role.value}
                        className={`cms-app-pill ${isSelected ? "is-selected" : ""}`}
                      >
                        <input
                          type="checkbox"
                          value={role.value}
                          {...register("roles", { required: true })}
                        />
                        <span className="cms-app-pill__inner">
                          <i className={`fas ${role.icon}`} />
                          {role.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="cms-app-pills">
                  {rolesAvailable.map((role) => {
                    const isSelected = targetRole === role.value;
                    return (
                      <label
                        key={role.value}
                        className={`cms-app-pill ${isSelected ? "is-selected" : ""}`}
                      >
                        <input
                          type="radio"
                          value={role.value}
                          {...register("targetRole", { required: true })}
                          disabled={systemLocked && editData?.roles?.[0] !== role.value}
                        />
                        <span className="cms-app-pill__inner">
                          <i className={`fas ${role.icon}`} />
                          {role.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="cms-settings-panel__divider" />

            <div className="cms-settings-panel__col cms-settings-panel__col--status">
              <span className="cms-settings-label">Publication</span>
              {isExistingCustom ? (
                <div className={`cms-status-row ${statusActive ? "is-active" : ""}`}>
                  <div className="cms-status-row__text">
                    <strong>{statusActive ? "Published" : "Draft"}</strong>
                    <span>
                      {statusActive ? "Visible in selected apps" : "Hidden from all apps"}
                    </span>
                  </div>
                  <div className="cms-switch-wrap">
                    <span className="cms-switch__label">{statusActive ? "ON" : "OFF"}</span>
                    <label className="cms-switch">
                      <input type="checkbox" {...register("status")} />
                      <span className="cms-switch__slider" />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="cms-status-row is-active">
                  <div className="cms-status-row__text">
                    <strong>Always Published</strong>
                    <span>Legal pages stay active for the selected app</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="cms-editor-wrap">
            <div className="cms-editor-wrap__head">
              <h5 className="cms-editor-wrap__title">
                <i className="fas fa-pen-fancy" />
                Content
              </h5>
              <span className="cms-editor-wrap__hint">Rich text · HTML supported</span>
            </div>
            <Controller
              name="content"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <JoditEditor
                  ref={editor}
                  value={value}
                  config={config}
                  tabIndex={1}
                  onBlur={(newContent) => onChange(newContent)}
                />
              )}
            />
          </div>
        </Modal.Body>

        <div className="cms-modal-footer">
          <button type="button" className="cms-btn-cancel" onClick={onHide}>
            Cancel
          </button>
          <button type="submit" className="cms-btn-save" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Saving...
              </>
            ) : editData ? (
              <>
                <i className="fas fa-check me-2" />
                Save Changes
              </>
            ) : (
              <>
                <i className="fas fa-plus me-2" />
                Create Document
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
